import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Search, GraduationCap, Building2, Sparkles, Wallet, TrendingUp, FileCheck } from "lucide-react";
import { Reveal, SectionHeading, Counter, Overline } from "./primitives";
import { INSTITUTIONS, CASE_STUDIES, STUDENT_JOURNEY, CITIES, COURSES, EXAMS, IMAGES } from "@/data/site";
import { fetchColleges } from "@/lib/api";
import { useModal } from "./Forms";

/* ---------------- Client Network ---------------- */
export function ClientNetwork() {
  const { openInstitution } = useModal();
  return (
    <section id="network" className="relative overflow-hidden bg-ink-1 py-28" data-testid="network">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading
            overline="04 — Our Network"
            title={<>TRUSTED BY <span className="text-gradient">EDUCATION INSTITUTIONS.</span></>}
            subtitle="Our growing network of business schools, colleges and universities."
          />
          <Reveal>
            <div className="flex items-center gap-3 rounded-2xl border border-brand-cyan/30 bg-brand-cyan/10 px-5 py-3">
              <span className="font-display text-3xl font-bold text-brand-cyan"><Counter to={50} suffix="+" /></span>
              <span className="font-mono text-[11px] uppercase leading-tight tracking-wider text-white/70">Institutions<br />&amp; Universities</span>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {INSTITUTIONS.map((inst, i) => (
            <Reveal key={inst.name} delay={(i % 4) * 0.05}>
              <div className="group flex h-full flex-col items-center justify-center rounded-2xl border border-white/10 bg-ink-2 p-6 text-center transition-all duration-300 hover:border-brand-cyan/40 hover:bg-white/[0.04]">
                <div className="flex h-16 w-16 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110">
                  <img src={inst.logo} alt={`${inst.name} logo`} className="max-h-full max-w-full object-contain" loading="lazy" data-testid={`institution-logo-${i}`} />
                </div>
                <p className="mt-4 font-display text-sm font-semibold leading-tight text-white">{inst.name}</p>
                <p className="mt-1 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-white/40">
                  <MapPin className="h-3 w-3" /> {inst.city}
                </p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.2}>
            <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-brand-cyan/30 bg-brand-cyan/5 p-6 text-center">
              <span className="font-display text-2xl font-bold text-brand-cyan">+50</span>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-white/60">Colleges &amp; Universities</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 flex justify-center">
            <button onClick={openInstitution} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 font-medium text-white transition-colors hover:border-brand-cyan/50 hover:text-brand-cyan" data-testid="view-network">
              View Our Education Network <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function initials(name) {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

/* ---------------- Case Studies ---------------- */
export function CaseStudies() {
  return (
    <section className="relative bg-ink-0 py-28" data-testid="case-studies">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          overline="05 — Client Success"
          title={<>DELIVERED ACROSS THE <span className="text-gradient">GROWTH JOURNEY.</span></>}
          subtitle="A snapshot of the capabilities we deliver for partner institutions."
        />
      </div>
      <div className="mt-14 flex gap-5 overflow-x-auto px-5 pb-6 lg:px-8" style={{ scrollbarWidth: "thin" }} data-testid="case-carousel">
        {CASE_STUDIES.map((c, i) => (
          <motion.div
            key={c.name}
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: (i % 3) * 0.08, duration: 0.6 }}
            className="group w-[320px] shrink-0 rounded-3xl border border-white/10 bg-ink-1 p-7 transition-colors duration-300 hover:border-brand-cyan/40"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 font-display font-bold text-brand-cyan">{initials(c.name)}</div>
              <div>
                <p className="font-display font-semibold text-white">{c.name}</p>
                <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-white/40"><MapPin className="h-3 w-3" />{c.city}</p>
              </div>
            </div>
            <div className="mt-5 space-y-2">
              <p className="font-mono text-[10px] uppercase tracking-wider text-white/40">Services Delivered</p>
              {c.services.map((s) => (
                <div key={s} className="flex items-center gap-2 text-sm text-white/70">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" /> {s}
                </div>
              ))}
            </div>
            <Link to={`/case-studies/${c.slug}`} className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-white/60 transition-colors group-hover:text-brand-cyan" data-testid={`case-study-link-${c.slug}`}>
              Explore Case Study <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Student College Search ---------------- */
export function StudentSearch() {
  const { openStudent, openAdmission } = useModal();
  const [colleges, setColleges] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("All");
  const [course, setCourse] = useState("All");
  const [exam, setExam] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchColleges({ q, city, course, exam })
      .then(setColleges)
      .finally(() => setLoading(false));
  }, [q, city, course, exam]);

  const examples = ["MBA Colleges in Delhi", "MBA Colleges in Bangalore", "MBA Colleges in Pune", "MBA Colleges in Mumbai", "MBA Colleges in Gurgaon", "MBA Colleges in Hyderabad"];

  return (
    <section id="students-search" className="relative overflow-hidden bg-ink-1 py-28" data-testid="student-search">
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          overline="For Students"
          title={<>YOUR DREAM COLLEGE STARTS WITH THE <span className="text-gradient">RIGHT DECISION.</span></>}
          subtitle="Explore MBA & PGDM colleges, compare your options and find an institution that fits your goals."
          align="center"
          className="mx-auto"
        />

        <div className="mx-auto mt-12 max-w-4xl rounded-3xl border border-white/10 bg-ink-2/80 p-5 backdrop-blur-xl lg:p-7">
          <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <Search className="h-5 w-5 text-brand-cyan" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search MBA / PGDM Colleges"
              className="w-full bg-transparent text-sm text-white outline-none placeholder:text-white/40"
              data-testid="college-search-input"
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <FilterSelect label="City" value={city} setValue={setCity} options={CITIES} testid="filter-city" />
            <FilterSelect label="Course" value={course} setValue={setCourse} options={COURSES} testid="filter-course" />
            <FilterSelect label="Entrance Exam" value={exam} setValue={setExam} options={EXAMS} testid="filter-exam" />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {examples.map((ex) => (
              <button
                key={ex}
                onClick={() => { setQ(ex.replace("MBA Colleges in ", "").replace("Gurgaon", "Gurugram")); }}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] text-white/55 transition-colors hover:border-brand-cyan/40 hover:text-brand-cyan"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>

        {/* results */}
        <div className="mx-auto mt-8 max-w-5xl">
          <p className="mb-4 font-mono text-xs uppercase tracking-wider text-white/40">
            {loading ? "Searching..." : `${colleges.length} ${colleges.length === 1 ? "college" : "colleges"} found`}
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {colleges.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (i % 4) * 0.05 }}
                className="group rounded-2xl border border-white/10 bg-ink-2 p-5 transition-colors hover:border-brand-cyan/40"
                data-testid="college-card"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">{c.name}</h3>
                    <p className="mt-1 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-white/40"><MapPin className="h-3 w-3" />{c.city} · {c.type}</p>
                  </div>
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 font-display font-bold text-brand-cyan">{initials(c.name)}</div>
                </div>
                <p className="mt-3 text-sm text-white/55">{c.short}</p>
                <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-white/60">
                  <span className="flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5 text-brand-cyan" />{c.courses.join(", ")}</span>
                  <span className="flex items-center gap-1"><Wallet className="h-3.5 w-3.5 text-brand-cyan" />{c.fees}</span>
                  <span className="flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-brand-cyan" />{c.exams.slice(0, 3).join(" / ")}</span>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <button onClick={() => openAdmission(c.name)} className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-4 py-2 text-xs font-semibold text-[#08120A] transition-shadow hover:shadow-[0_0_18px_rgba(198,255,58,0.4)]" data-testid="college-apply-now">
                    <FileCheck className="h-3.5 w-3.5" /> Apply Now
                  </button>
                  <button onClick={openStudent} className="inline-flex items-center gap-1.5 text-sm font-medium text-white/70 transition-colors group-hover:text-brand-cyan">
                    Connect with a counsellor <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          {!loading && colleges.length === 0 && (
            <p className="py-10 text-center text-sm text-white/40">No colleges match your filters. Try adjusting your search.</p>
          )}
        </div>

        <div className="mt-10 flex justify-center">
          <button onClick={openStudent} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-8 py-3.5 font-medium text-[#08120A] transition-shadow hover:shadow-[0_0_30px_rgba(198,255,58,0.45)]" data-testid="explore-colleges">
            Explore Colleges <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

function FilterSelect({ label, value, setValue, options, testid }) {
  return (
    <div>
      <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-wider text-white/40">{label}</label>
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-brand-cyan/60"
        data-testid={testid}
      >
        {options.map((o) => <option key={o} value={o} className="bg-ink-1">{o}</option>)}
      </select>
    </div>
  );
}

/* ---------------- Student Journey ---------------- */
export function StudentJourney() {
  const { openStudent } = useModal();
  return (
    <section className="relative bg-ink-0 py-28" data-testid="student-journey">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          overline="Student Journey"
          title={<>FROM CONFUSION TO <span className="text-gradient">CONFIDENCE.</span></>}
        />
        <Reveal>
          <div className="relative mt-12 h-64 overflow-hidden rounded-3xl border border-white/10" data-testid="journey-banner">
            <img src={IMAGES.graduation} alt="Students celebrating graduation" className="h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink-0/95 via-ink-0/65 to-ink-0/35" />
            <div className="absolute bottom-6 left-6 max-w-sm">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-cyan">Your future, guided</p>
              <p className="mt-2 font-display text-2xl font-semibold leading-tight text-white">Thousands of students guided to the right college every year.</p>
            </div>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {STUDENT_JOURNEY.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.08}>
              <div className="relative h-full rounded-2xl border border-white/10 bg-ink-1 p-6">
                <span className="font-display text-4xl font-bold text-brand-cyan/25">{step.n}</span>
                <h3 className="mt-3 font-display text-lg font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-sm text-white/50">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <button onClick={openStudent} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-8 py-3.5 font-medium text-[#08120A] transition-shadow hover:shadow-[0_0_30px_rgba(198,255,58,0.45)]" data-testid="find-my-college">
            Find My College <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
